<template>
    <div class="area">
        <header-message>Calibration In Progress</header-message>

        <header-message class="mt-5 mb-5"><fa-icon icon="spinner" class="fa-spin" /></header-message>

        <header-message class="text-danger">Do not move Auggie or Vehicle</header-message>

        <img v-show="imageSource" :src="imageSource" class="img-fluid" alt="Target">
    </div>
</template>

<style scoped lang="scss">
    svg.svg-inline--fa.fa-w-16 {
        width:5rem;
        height:5rem;
    }
    .area {
        position:relative;
        img {
            margin: .1em;
            height: 25vh;
            position: absolute;
            left: 0;
            top: 2.0em;
        }
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";

import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";
import { SignalR } from "@/support/SignalR";
import Toast from "@/shared/support/Toast";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { AUGGIEAVAILABLE } from "@/main";
import Utility from "@/shared/support/Utility";
import { Global } from "@/support/GlobalData";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";
import { CalibrationType } from "@/shared/enums/CalibrationType";

export default defineComponent({
    name: "CalibrationInProgress",
    mixins: [PageBase],
    components: {
        HeaderMessage,
    },
    data() {
        return {
            imageSource: "",
            targetNumber: -1,
        };
    },
    methods: {
        renderTargetImage(targetImageUploadId: number, targetNumber: number): void {

            this.imageSource = "";
            this.targetNumber = targetNumber;
            Toast.info("New target image received");

            let url = "";
            if (Global.IsDemo()) {
                url = Utility.formatUrl(`/demo/targets/target${targetImageUploadId}.png`, {});
            } else {
                url = Utility.formatUrl(`/Service/TargetRenderer/GetContentById/${targetImageUploadId}`, {}, Global.GetWebAPIUrl(), true);
            }

            if (AUGGIEAVAILABLE) {
                this.imageSource = Auggie.Target.ShowTarget(url);
            } else {
                this.imageSource = url;
            }

            // Capture ambient light only for the first target image
            if (Global.AmbientLightTarget == null) {
                this.captureAmbientLightTarget();
            }

            this.getCurrentStatusForListeners();
        },

        captureAmbientLightTarget(): void {
            // Capture ambient light sensor value during first target display
            if (!AUGGIEAVAILABLE) return;
            if (!Global.IsVersionAtLeast("2026.15.1")) return;
            try {
                const infoString = Auggie.Device.AmbientLightInfo();
                if (infoString?.length > 0) {
                    const info = JSON.parse(infoString);
                    if (info && info.AmbientLight != null) {
                        Global.AmbientLightTarget = info.AmbientLight;
                        CalibrationEventSupport.sendAmbientLightInformation();
                    }
                }
            } catch (error) {
                console.error("Error capturing ambient light target:", error);
            }
        },

        clearTargetImage(): void {
            if (AUGGIEAVAILABLE) {
                Auggie.Target.ClearTarget();
            } else {
                // nothing to do
            }
        },

        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationInProgress",
                TargetNumber: this.targetNumber,
            } as DeviceStatusDto);
        }
    },
    mounted(): void {
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onTechnicianCameraRejected = (): void => {
            this.clearTargetImage();
            Toast.info("The technician requested repositioning and verifying the camera.");
            this.$router.push("/Calibration/CameraSetup");
        };
        SignalR.onCalibrationSuccess = (): void => {
            this.clearTargetImage();
            this.$router.push("/Calibration/Complete");
        };

        SignalR.onCalibrationFailed = (): void => {
            this.clearTargetImage();
            this.$router.push("/Calibration/Failed");
        };
        SignalR.onVehicleTargetImageReady = (targetImageUploadId: number, targetNumber: number): void => {
            this.renderTargetImage(targetImageUploadId, targetNumber);
        };
        if (Global.IsDemo()) {
            let total = 1;
            if (Global.CalibrationSession?.CalibrationType === CalibrationType.Sequential)
                total = 3;
            for (let count = 0 ; count < total ; ++count) {
                setTimeout((): void => {
                    this.renderTargetImage(count, count);
                }, count * Number(import.meta.env.VITE_DEMODELAY!) * 1000);
            }
            setTimeout((): void => {
                this.clearTargetImage();
                this.$router.push("/Calibration/Complete");
            }, total * Number(import.meta.env.VITE_DEMODELAY!) * 1000);
        }
        this.getCurrentStatusForListeners();
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        this.clearTargetImage();
        SignalR.onTechnicianCameraRejected = null;
        SignalR.onCalibrationSuccess = null;
        SignalR.onCalibrationFailed = null;
        SignalR.onVehicleTargetImageReady = null;
    }
});
</script>
